import { Accordion, Anchor, Grid, Separator } from '@design-systems/canvas-ui'
import { MPMLayout } from 'components/layout'
import { useNavigate } from 'react-router-dom'
import { clearLocationParam, getDeviceSummary } from 'utils/helpers'
import { useStoreFacts } from 'hooks/store-facts/useStoreFacts'
import { BasicPlaceholder } from 'components/placeholder'
import { useStoreLeadership } from 'hooks/store-facts/useStoreLeadership'
import {
    formatStoreTime,
    mapFieldNames,
    translateStoreFactsApiString,
    formatDateWithLocale,
} from './helpers'
import { isStore } from 'utils/location'
import { MapModal } from './map-modal'
import Icon, { ProcessingIcon, StoreIcon } from '@design-systems/react-icons'
import { useDeviceInfo } from 'hooks/utils/useDeviceInfo'
import { ErrorAlert, ErrorFallback } from 'components/error-fallback'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useEffect, useState, useCallback } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useUpdatePreferences } from 'hooks/user-context/useUpdatePreferences'
import { useCreatePreferences } from 'hooks/user-context/useCreatePreferences'
import { PreferredMap } from 'types/user-context'
import './store-facts.scss'
import { formatCurrency } from 'utils/currency-helper'

export const StoreFactsPage = () => {
    const { t, params, setParams } = useEssentials()
    const locationParams = params.get('locationId')
    const { storeFacts } = TRANSLATION_KEYS

    window.onpopstate = (e) => {
        clearLocationParam({ params, setParams })
    }

    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={t(storeFacts.title)}
                />
            )}
        >
            <MPMLayout heading={t(storeFacts.title)} breadcrumbsEnabled={false}>
                <StoreFacts key={locationParams} />
            </MPMLayout>
        </ErrorBoundary>
    )
}

const StoreFacts = () => {
    const navigate = useNavigate()
    const {
        t,
        env,
        session,
        userAgent,
        currentLocation,
        i18n,
        preferences,
        setPreferences,
    } = useEssentials()
    const {
        contact,
        location: locationKeys,
        storeFacts: {
            quickFacts,
            lastYearSales,
            originatedSales,
            fulfilledSales,
            totalSales,
            hours,
            keyDates,
            opened,
            lastRemodel,
            notAvailable,
            packStation: packStationKey,
            leadership,
            store,
            role,
            name,
            since,
            capabilities: capabilitiesKey,
            field: fieldKey,
            inboundNodes,
            outboundNodes,
        },
    } = TRANSLATION_KEYS

    const deviceSummary = getDeviceSummary()
    const isIphone = deviceSummary.device === 'iPhone'

    const [showMapDialog, setShowMapDialog] = useState(false)
    const [pendingMapUrl, setPendingMapUrl] = useState<{
        latitude: number
        longitude: number
    } | null>(null)
    const [selectedMap, setSelectedMap] = useState<PreferredMap>('Apple')

    const appContext = preferences?.user_contexts?.[0]?.app_context
    const currentPreferredMap = appContext?.preferredMap
    const currentLanguage = appContext?.language || 'en'
    const currentLandingPage = appContext?.landingPage

    const { lanId } = session?.userInfo ?? { lanId: '' }

    const { createPreferences, isLoading: isCreateLoading } =
        useCreatePreferences(
            { lanId, env, accessToken: session?.accessToken ?? '' },
            (response: any) => {
                const newPreferences = {
                    ...preferences,
                    user_contexts: [response],
                }
                setPreferences(newPreferences)
            },
        )

    const { updatePreferences, isLoading: isUpdateLoading } =
        useUpdatePreferences({
            lanId,
            env,
            accessToken: session?.accessToken ?? '',
            preferences,
        })

    useEffect(() => {
        if (!isStore(currentLocation)) {
            void navigate('/')
        }
    }, [navigate, currentLocation])

    const handleMapSelection = useCallback(
        (mapChoice: PreferredMap) => {
            const payload = {
                language: currentLanguage,
                preferredMap: mapChoice,
                ...(currentLandingPage && { landingPage: currentLandingPage }),
            }

            const onSuccess = (response: any) => {
                const newPreferences = {
                    ...preferences,
                    user_contexts: [response],
                }
                setPreferences(newPreferences)
                setShowMapDialog(false)

                // Open map after preference is saved
                if (pendingMapUrl) {
                    const mapUrl = buildMapUrl(
                        mapChoice,
                        pendingMapUrl.latitude,
                        pendingMapUrl.longitude,
                    )
                    window.location.href = mapUrl
                    setPendingMapUrl(null)
                }
            }

            if (preferences.user_contexts.length === 0) {
                createPreferences(payload, { onSuccess })
            } else {
                updatePreferences(payload, { onSuccess })
            }
        },
        [
            currentLanguage,
            currentLandingPage,
            preferences,
            setPreferences,
            createPreferences,
            updatePreferences,
            pendingMapUrl,
        ],
    )

    const buildMapUrl = (
        mapType: string,
        latitude: number,
        longitude: number,
    ): string => {
        if (mapType === 'Apple') {
            return `maps://maps.apple.com/?q=${latitude},${longitude}`
        } else {
            return `https://maps.google.com/maps?q=${latitude},${longitude}`
        }
    }

    const handleMapLinkClick = useCallback(
        (e: React.MouseEvent, latitude: number, longitude: number) => {
            if (isIphone && !currentPreferredMap) {
                e.preventDefault()
                setPendingMapUrl({ latitude, longitude })
                setShowMapDialog(true)
            }
        },
        [isIphone, currentPreferredMap],
    )

    const { data, isLoading, isError, error, refetch } = useStoreFacts({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })
    const { data: leadershipData, isLoading: leadershipLoading } =
        useStoreLeadership({
            env,
            accessToken: session?.accessToken ?? '',
            location: currentLocation,
        })
    const { isAppleDevice } = useDeviceInfo(userAgent)

    if (isLoading || leadershipLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="table" rows={5} columns={5} />
            </div>
        )
    }
    if (isError || !data) {
        return <ErrorAlert error={error} onRetry={() => refetch()} />
    }

    const { basic, capabilities, locationDetails, supply_chain_relationships } =
        data[0]
    const {
        addressDetails,
        contactDetails,
        region_group_district,
        last_year_sales,
        operating_hours,
        dates,
    } = basic
    const { address, city, region, pincode, country, latitude, longitude } =
        addressDetails
    const { contact_main, contact_fax } = contactDetails
    const { region_id, group_id, district_id } = region_group_district
    const { originated_sales, fulfilled_sales, total_sales } = last_year_sales
    const { time_zone_code, regular_hours } = operating_hours
    const { days = [] } = regular_hours ?? {}
    const { open_date, last_remodel_date } = dates

    const { location_id, location_name, store_type } = locationDetails

    const columns =
        leadershipData?.card_config?.card_query_attribute?.columns ?? []
    const mappedLeadership = mapFieldNames(
        columns,
        leadershipData?.query_results ?? [],
    )

    const mappedLeadershipField = mappedLeadership.filter(
        (l) => l.leader_type === 'field',
    )
    const mappedLeadershipStore = mappedLeadership.filter(
        (l) => l.leader_type === 'store',
    )

    const formattedFullDate = (dateStr: any) => {
        try {
            return formatDateWithLocale(dateStr, 'MMMM d, yyyy', i18n.language)
        } catch {
            return t(notAvailable)
        }
    }

    const formattedShortDate = (dateStr: any) => {
        try {
            return formatDateWithLocale(dateStr, 'MM/dd/yy', i18n.language)
        } catch {
            return t(notAvailable)
        }
    }

    let packStation = null
    if (basic.pack_stations) {
        packStation = {
            label: t(packStationKey),
            value: basic.pack_stations,
        }
    }

    // Build map URL based on device and preference
    let mapUrl: string
    if (isIphone && currentPreferredMap) {
        mapUrl = buildMapUrl(currentPreferredMap, latitude, longitude)
    } else if (isAppleDevice) {
        mapUrl = `http://maps.apple.com/?q=${latitude},${longitude}`
    } else {
        mapUrl = `https://maps.google.com/maps?q=${latitude},${longitude}`
    }

    return (
        <>
            <StoreFactLine
                value={
                    <span className="sf-org flex-ac">
                        <Icon
                            svg={StoreIcon}
                            size="lg"
                            className="hc8-pt-2x"
                            aria-hidden
                        />
                        <span className="sf-org-name">
                            <span>
                                {location_id} {location_name}
                            </span>
                            <span>
                                {translateStoreFactsApiString(t, store_type)}
                            </span>
                        </span>
                    </span>
                }
            />
            <br />
            <StoreFactHeading value={t(quickFacts)} />
            <StoreFactLine
                value={
                    <Anchor
                        href={mapUrl}
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                            handleMapLinkClick(e, latitude, longitude)
                        }
                    >
                        {`${address}`}
                        <br />
                        {`${city}, ${region} ${pincode}`}
                    </Anchor>
                }
            />
            <StoreFactLine value={`${country} ${t(locationKeys.county)}`} />
            <br />
            <StoreFactLine
                value={
                    <>
                        <span>
                            {t(contact.mainPhone)}
                            {': '}
                        </span>
                        <StoreFactPhone value={contact_main} />
                    </>
                }
            />
            <StoreFactLine
                value={
                    <>
                        <span>
                            {t(contact.fax)}
                            {': '}
                        </span>
                        <StoreFactPhone value={contact_fax} />
                    </>
                }
            />
            <br />
            <StoreFactLine value={`${t(locationKeys.region)} ${region_id}`} />
            <StoreFactLine value={`${t(locationKeys.group)} ${group_id}`} />
            <StoreFactLine
                value={`${t(locationKeys.district)} ${district_id}`}
            />
            <br />
            {(supply_chain_relationships?.inbound ?? []).length > 0 && (
                <>
                    <Accordion
                        className="hc8-mv-none"
                        spacing="dense"
                        headerSlot={
                            <StoreFactSubheading value={t(inboundNodes)} />
                        }
                    >
                        <ul className="hc-list-reset">
                            {(supply_chain_relationships?.inbound ?? []).map(
                                (node) => (
                                    <li
                                        key={node.node_id}
                                        className="hc8-mv-3x"
                                    >
                                        <StoreFactLine
                                            value={`${node.node_id} - ${node.node_name} - ${node.node_type}`}
                                        />
                                    </li>
                                ),
                            )}
                        </ul>
                    </Accordion>
                    <br />
                </>
            )}
            {(supply_chain_relationships?.outbound ?? []).length > 0 && (
                <>
                    <Accordion
                        className="hc8-mv-none"
                        spacing="dense"
                        headerSlot={
                            <StoreFactSubheading value={t(outboundNodes)} />
                        }
                    >
                        <ul className="hc-list-reset">
                            {(supply_chain_relationships?.outbound ?? []).map(
                                (node) => (
                                    <li
                                        key={node.node_id}
                                        className="hc8-mv-3x"
                                    >
                                        <StoreFactLine
                                            value={`${node.node_id} - ${node.node_name} - ${node.node_type}`}
                                        />
                                    </li>
                                ),
                            )}
                        </ul>
                    </Accordion>
                    <br />
                </>
            )}
            <StoreFactHeading value={t(lastYearSales)} />
            <StoreFactLine
                value={`${t(originatedSales)}: ${formatCurrency(originated_sales)}`}
            />
            <StoreFactLine
                value={`${t(fulfilledSales)}: ${formatCurrency(fulfilled_sales)}`}
            />
            <StoreFactLine
                value={`${t(totalSales)}: ${formatCurrency(total_sales)}`}
            />
            <br />
            <StoreFactHeading
                value={`${t(hours)} ${translateStoreFactsApiString(t, time_zone_code)}`}
            />
            <div>
                {days.map((d: any, index: number) => {
                    const dayLabel =
                        index === 0 || d.day !== days[index - 1].day
                            ? `${translateStoreFactsApiString(t, d.day.toLocaleLowerCase())}:`
                            : ''
                    if (!d.begin_time || !d.end_time) {
                        return (
                            <StoreFactHours
                                day={`${translateStoreFactsApiString(t, d.day)}`}
                                value={`${t('Closed')}`}
                                key={d.day}
                                index={index}
                            />
                        )
                    }
                    const openTime = formatStoreTime(d.begin_time)
                    const closeTime = formatStoreTime(d.end_time)
                    return (
                        <StoreFactHours
                            day={dayLabel}
                            value={`${openTime} to ${closeTime}`}
                            key={d.day}
                            index={index}
                        />
                    )
                })}
            </div>
            <br />
            <StoreFactHeading value={t(keyDates)} />
            <StoreFactLine
                value={`${t(opened)} ${formattedFullDate(open_date)}`}
            />
            <StoreFactLine
                value={`${t(lastRemodel)} ${formattedFullDate(last_remodel_date)}`}
            />
            <br />
            <StoreFactExpandable value={t(leadership)}>
                {leadershipLoading ? (
                    <Icon svg={ProcessingIcon} aria-hidden />
                ) : (
                    <>
                        <StoreFactSubheading value={t(store)} />
                        <Grid.Container>
                            <Grid.Item
                                className="hc8-pv-3x hc8-pr-none"
                                size={{
                                    xs: 4,
                                }}
                            >
                                {t(role)}
                            </Grid.Item>
                            <Grid.Item
                                className="hc8-pv-3x hc8-ph-none"
                                size={{
                                    xs: 5,
                                }}
                            >
                                {t(name)}
                            </Grid.Item>
                            <Grid.Item
                                className="hc8-pv-3x hc8-pl-none"
                                size={{
                                    xs: 3,
                                }}
                            >
                                {t(since)}
                            </Grid.Item>
                        </Grid.Container>
                        <>
                            {mappedLeadershipStore.map((l, i) => {
                                return (
                                    <Grid.Container key={l.employee_name}>
                                        <Grid.Item
                                            className="hc8-pv-1x hc8-pr-none"
                                            size={{
                                                xs: 4,
                                            }}
                                        >
                                            {l.employee_job_title}
                                        </Grid.Item>
                                        <Grid.Item
                                            className="hc8-pv-1x hc8-ph-none"
                                            size={{
                                                xs: 5,
                                            }}
                                        >
                                            <Anchor
                                                href={`mailto:${l.email_address}`}
                                            >
                                                {l.employee_name}
                                            </Anchor>
                                        </Grid.Item>
                                        <Grid.Item
                                            className="hc8-pv-1x hc8-pl-none"
                                            size={{
                                                xs: 3,
                                            }}
                                        >
                                            {formattedShortDate(
                                                l.position_start_date,
                                            )}
                                        </Grid.Item>
                                    </Grid.Container>
                                )
                            })}
                        </>
                        <br />
                        <StoreFactSubheading value={t(fieldKey)} />
                        <Grid.Container>
                            <Grid.Item
                                className="hc8-pv-3x hc8-pr-none"
                                size={{
                                    xs: 4,
                                }}
                            >
                                {t(role)}
                            </Grid.Item>
                            <Grid.Item
                                className="hc8-pv-3x hc8-pl-none"
                                size={{
                                    xs: 8,
                                }}
                            >
                                {t(name)}
                            </Grid.Item>
                        </Grid.Container>
                        <>
                            {mappedLeadershipField.map((l, i) => {
                                return (
                                    <Grid.Container key={l.employee_name}>
                                        <Grid.Item
                                            className="hc8-pv-1x hc8-pr-none"
                                            size={{
                                                xs: 4,
                                            }}
                                        >
                                            {l.employee_job_title}
                                        </Grid.Item>
                                        <Grid.Item
                                            className="hc8-pv-1x hc8-pl-none"
                                            size={{
                                                xs: 8,
                                            }}
                                        >
                                            <Anchor
                                                href={`mailto:${l.email_address}`}
                                            >
                                                {l.employee_name}
                                            </Anchor>
                                        </Grid.Item>
                                    </Grid.Container>
                                )
                            })}
                        </>
                    </>
                )}
            </StoreFactExpandable>
            <br />
            <StoreFactHeading value={t(capabilitiesKey)} />
            {packStation && (
                <>
                    <p className="hc8-mv-3x flex-sb">
                        <span>{packStation.label}</span>
                        <span>{packStation.value}</span>
                    </p>
                    <Separator />
                </>
            )}
            <>
                {capabilities.map((c: any, i: number) => {
                    const isNotFirst = i > 0

                    const isShipFromStore =
                        c?.capability_code === 'Ship from Store' ||
                        c?.capability_name === 'Ship from Store'

                    const hasPriorityStoreYes =
                        Array.isArray(c?.capability_attributes) &&
                        c.capability_attributes.some(
                            (a: any) =>
                                a?.attribute_name === 'Priority Store' &&
                                String(a?.attribute_value).toLowerCase() ===
                                    'yes',
                        )

                    return (
                        <div key={i}>
                            {isNotFirst && <Separator />}
                            <StoreFactCapability {...c} />
                            {isShipFromStore && hasPriorityStoreYes && (
                                <>
                                    <Separator />
                                    <StoreFactCapabilityLine
                                        value="Priority Store"
                                        className="hc8-mv-3x"
                                    />
                                </>
                            )}
                        </div>
                    )
                })}
            </>
            <MapModal
                isVisible={showMapDialog}
                onHide={() => setShowMapDialog(false)}
                selectedMap={selectedMap}
                onMapSelect={setSelectedMap}
                onContinue={() => {
                    handleMapSelection(selectedMap)
                    setShowMapDialog(false)
                }}
                isLoading={isCreateLoading || isUpdateLoading}
            />
        </>
    )
}

type StoreFaceLineType = {
    value: string | React.ReactNode
}
type StoreFaceHourType = {
    day: string
    value: string | React.ReactNode
    index: number
}
type StoreFaceExpandedType = StoreFaceLineType & {
    children: React.ReactNode | React.ReactNode[]
}

type DayOperatingHours = {
    day: string
    begin_time: string | null
    end_time: string | null
    is_open: boolean
    open_flag: boolean
    close_flag: boolean
}
type OperatingHours = DayOperatingHours[]
type ContactDetails = {
    contact_main: string
    contact_fax: string
}
type StoreFaceCapabilityType = {
    capability_name: string
    contactDetails?: ContactDetails
    capability_hours?: {
        effective_date: string
        expiration_date: string
        days: OperatingHours
    }
    timezone: string
}
type StoreFaceCapabilityLineType = StoreFaceLineType & {
    className: string
}
const StoreFactHeading = ({ value }: StoreFaceLineType) => <h2>{value}</h2>
const StoreFactSubheading = ({ value }: StoreFaceLineType) => <h2>{value}</h2>
const StoreFactLine = ({ value }: StoreFaceLineType) => (
    <p className="hc8-ma-none">{value}</p>
)
const StoreFactPhone = ({ value }: StoreFaceLineType) => (
    <Anchor href={`tel:${value}`}>{value}</Anchor>
)
const StoreFactHours = ({ day, value, index }: StoreFaceHourType) => {
    const verticalPadding = index === 0 ? 'hc8-pt-3x hc8-pb-1x' : 'hc8-pv-1x'
    return (
        <Grid.Container>
            <Grid.Item
                className={`${verticalPadding} hc8-pr-none`}
                size={{
                    xs: 4,
                }}
            >
                {day}
            </Grid.Item>
            <Grid.Item
                className={`${verticalPadding} hc8-pr-none`}
                size={{
                    xs: 8,
                }}
            >
                {value}
            </Grid.Item>
        </Grid.Container>
    )
}
const StoreFactExpandable = ({ value, children }: StoreFaceExpandedType) => {
    return (
        <Accordion
            spacing="dense"
            headerSlot={<StoreFactHeading value={value} />}
        >
            {children}
        </Accordion>
    )
}

const StoreFactCapabilityLine = ({
    value,
    className,
}: StoreFaceCapabilityLineType) => <p className={className}>{value}</p>
const StoreFactCapability = (capability: StoreFaceCapabilityType) => {
    const { contact } = TRANSLATION_KEYS
    const { t } = useEssentials()
    const capabilityContainerClass = 'hc8-mv-3x'

    const { capability_name, contactDetails, capability_hours } = capability

    if (!capability_hours && !contactDetails) {
        return (
            <StoreFactCapabilityLine
                value={translateStoreFactsApiString(t, capability_name)}
                className={capabilityContainerClass}
            />
        )
    }

    return (
        <Accordion
            className={capabilityContainerClass}
            spacing="dense"
            headerSlot={
                <StoreFactCapabilityLine
                    value={translateStoreFactsApiString(t, capability_name)}
                    className="hc8-mv-none"
                />
            }
        >
            {contactDetails && (
                <>
                    {contactDetails.contact_main && (
                        <StoreFactLine
                            value={
                                <>
                                    <span>
                                        {t(contact.mainPhone)}
                                        {': '}
                                    </span>
                                    <StoreFactPhone
                                        value={contactDetails.contact_main}
                                    />
                                </>
                            }
                        />
                    )}
                    {contactDetails.contact_fax && (
                        <StoreFactLine
                            value={
                                <>
                                    <span>
                                        {t(contact.fax)}
                                        {': '}
                                    </span>
                                    <StoreFactPhone
                                        value={contactDetails.contact_fax}
                                    />
                                </>
                            }
                        />
                    )}
                    <br />
                </>
            )}
            {capability_hours && (
                <>
                    {capability_hours.days.map((d, index: number) => {
                        const key = `${capability_name}_${d.day}_${index}`
                        const dayLabel =
                            index === 0 ||
                            d.day !== capability_hours.days[index - 1].day
                                ? `${translateStoreFactsApiString(t, d.day.toLocaleLowerCase())}:`
                                : ''
                        if (!d.begin_time || !d.end_time) {
                            return (
                                <StoreFactHours
                                    day={`${translateStoreFactsApiString(t, d.day)}`}
                                    value={`${t('Closed')}`}
                                    key={`${key}_${index}`}
                                    index={index}
                                />
                            )
                        }
                        const openTime = formatStoreTime(d.begin_time)
                        const closeTime = formatStoreTime(d.end_time)
                        return (
                            <StoreFactHours
                                day={dayLabel}
                                value={`${openTime} to ${closeTime}`}
                                key={`${key}_${index}`}
                                index={index}
                            />
                        )
                    })}
                </>
            )}
        </Accordion>
    )
}
